import { z } from 'zod';

// --- Zod Schemas ---

export const quickLinkSchema = z.object({
  id: z.string().min(1).regex(/^[a-z0-9-]+$/, 'ID must be lowercase alphanumeric with hyphens'),
  name: z.string().min(1).max(100),
  description: z.string().max(300),
  url: z.string().url(),
  icon: z.string().min(1),
  category: z.string().max(50).optional(),
});

export const quickLinksConfigSchema = z.object({
  version: z.string(),
  links: z.array(quickLinkSchema),
});

// --- TypeScript Interfaces ---

export interface QuickLink {
  id: string;
  name: string;
  description: string;
  url: string;
  icon: string;
  category?: string;
}

export interface QuickLinksConfig {
  version: string;
  links: QuickLink[];
}

// --- Parser ---

/**
 * Parses raw config, returns only valid and deduplicated entries.
 * Logs a warning for each invalid or duplicate entry.
 */
export function parseQuickLinks(raw: unknown): QuickLink[] {
  let entries: unknown[];

  const result = quickLinksConfigSchema.safeParse(raw);

  if (result.success) {
    entries = result.data.links;
  } else {
    // Schema-level failure — try to salvage individual entries
    if (typeof raw !== 'object' || raw === null || !('links' in raw)) {
      console.warn('[QuickLinks] Invalid config: missing or malformed "links" array');
      return [];
    }

    const rawLinks = (raw as Record<string, unknown>).links;
    if (!Array.isArray(rawLinks)) {
      console.warn('[QuickLinks] Invalid config: "links" is not an array');
      return [];
    }

    const valid: QuickLink[] = [];
    for (const entry of rawLinks) {
      const entryResult = quickLinkSchema.safeParse(entry);
      if (entryResult.success) {
        valid.push(entryResult.data as QuickLink);
      } else {
        const entryId =
          typeof entry === 'object' && entry !== null && 'id' in entry
            ? String((entry as Record<string, unknown>).id)
            : 'unknown';
        const reasons = entryResult.error.issues
          .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
          .join('; ');
        console.warn(`[QuickLinks] Invalid entry "${entryId}": ${reasons}`);
      }
    }
    entries = valid;
  }

  // Deduplicate by id — first occurrence wins
  const seen = new Set<string>();
  const deduplicated: QuickLink[] = [];

  for (const entry of entries) {
    const link = entry as QuickLink;
    if (seen.has(link.id)) {
      console.warn(`[QuickLinks] Duplicate id "${link.id}" — skipping`);
      continue;
    }
    seen.add(link.id);
    deduplicated.push(link);
  }

  return deduplicated;
}

// --- Printer ---

/**
 * Converts a validated QuickLink array back into a QuickLinksConfig object.
 */
export function printQuickLinks(links: QuickLink[]): QuickLinksConfig {
  return {
    version: '1.0',
    links: links.map((link) => ({ ...link })),
  };
}

// --- Grouping ---

/**
 * Groups links by category. Links without a category use `undefined` as the key.
 */
export function groupByCategory(links: QuickLink[]): Map<string | undefined, QuickLink[]> {
  const groups = new Map<string | undefined, QuickLink[]>();
  for (const link of links) {
    const key = link.category;
    const group = groups.get(key) ?? [];
    group.push(link);
    groups.set(key, group);
  }
  return groups;
}
